import * as React from 'react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { rankItem } from '@tanstack/match-sorter-utils'

import { useTranslation } from '@kostbar/i18n'

// import { Alert } from '@/components/ui/alert'
import { AlertCircle, Inbox, RefreshCw } from 'lucide-react'
import { useDebounce } from './hooks/use-debounce'

import { Toolbar } from './toolbar'
import { DataTablePagination } from './pagination'
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  densityRowClasses,
} from './utils'
import { logger } from './utils/logger'
import type { DataTableProps, Density, Sort } from './types'
import type {
  ColumnSizingState,
  FilterFn,
  Row,
  SortingState,
  Table as TanStackTable,
  VisibilityState,
} from '@tanstack/react-table'
import { Spinner } from '@/components/ui/spinner'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const fuzzyFilter: FilterFn<unknown> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

// Ghost line indicator shown during column resize
function ColumnResizeIndicator<TData>({
  table,
  selectable,
}: {
  table: TanStackTable<TData>
  selectable: boolean
}) {
  const { columnSizingInfo } = table.getState()
  const isResizing = columnSizingInfo.isResizingColumn

  if (!isResizing) return null

  // Calculate the position of the ghost line
  // Start with checkbox column width if selectable
  let leftOffset = selectable ? 48 : 0 // w-12 = 48px

  // Sum up widths of columns before the one being resized
  const headers = table.getFlatHeaders()
  for (const header of headers) {
    if (header.column.id === isResizing) {
      // Add the current column width + the delta
      leftOffset += header.getSize() + (columnSizingInfo.deltaOffset ?? 0)
      break
    }
    leftOffset += header.getSize()
  }

  return (
    <div
      className="absolute top-0 bottom-0 w-0.5 bg-primary z-20 pointer-events-none"
      style={{
        left: leftOffset,
        transition: 'none',
      }}
    />
  )
}

// Memoized row component to prevent unnecessary re-renders
const DataTableRow = React.memo(function DataTableRow<TData>({
  row,
  density,
  selectable,
  rowActions,
  onRowClick,
  isSelected,
}: {
  row: Row<TData>
  density: Density
  selectable: boolean
  rowActions?: (row: Row<TData>) => React.ReactNode
  onRowClick?: (row: Row<TData>) => void
  isSelected: boolean
}) {
  const handleRowClick = React.useCallback(() => {
    onRowClick?.(row)
  }, [row.id, onRowClick])

  const handleSelection = React.useCallback(
    (v: boolean | 'indeterminate') => {
      row.toggleSelected(!!v)
    },
    [row.id],
  )

  const handleCheckboxClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleActionsClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  return (
    <TableRow
      key={row.id}
      data-state={isSelected && 'selected'}
      className={densityRowClasses[density]}
      onClick={onRowClick ? handleRowClick : undefined}
    >
      {selectable && (
        <TableCell>
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleSelection}
            aria-label="Select row"
            onClick={handleCheckboxClick}
          />
        </TableCell>
      )}
      {row.getVisibleCells().map((cell) => (
        <TableCell key={cell.id} style={{ width: cell.column.getSize() }}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
      {rowActions && (
        <TableCell onClick={handleActionsClick}>{rowActions(row)}</TableCell>
      )}
    </TableRow>
  )
}) as <TData>(props: {
  row: Row<TData>
  density: Density
  selectable: boolean
  rowActions?: (row: Row<TData>) => React.ReactNode
  onRowClick?: (row: Row<TData>) => void
  isSelected: boolean
}) => React.ReactElement

export function DataTable<TData, TValue = unknown>(
  props: DataTableProps<TData, TValue>,
) {
  const {
    columns,
    data,
    fetcher,
    infiniteFetcher,
    layout = 'paginated',
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    initialState,
    resizingMode = 'onEnd',
    density: densityProp = 'normal',
    showDensityToggle = true,
    selectable = true,
    enableColumnVisibility = true,
    filters,
    globalSearch = true,
    rowActions,
    bulkActions,
    onRowClick,
    onSelectionChange,
    inlineBulkBar = true,
    queryKey: queryKeyProp,
    getRowId,
    emptyState,
    loading,
    error,
  } = props
  const { t: tUi } = useTranslation('ui')

  const [sorting, setSorting] = React.useState<SortingState>(
    initialState?.sorting ?? [],
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(initialState?.columnVisibility ?? {})
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({})

  const [rowSelection, setRowSelection] = React.useState<
    Record<string, boolean>
  >({})
  const [density, setDensity] = React.useState<Density>(densityProp)
  const [pageIndex, setPageIndex] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(
    initialState?.pageSize ?? DEFAULT_PAGE_SIZE,
  )
  const [globalQuery, setGlobalQuery] = React.useState<string | undefined>(
    undefined,
  )
  const [filterValues, setFilterValues] = React.useState<
    Record<string, unknown>
  >({})

  // Debounce search query to avoid excessive API calls while typing
  const debouncedGlobalQuery = useDebounce(globalQuery, 300)
  // Debounce filter values to avoid excessive API calls while typing in text filters
  const debouncedFilterValues = useDebounce(filterValues, 300)

  const handleFilterChange = React.useCallback((id: string, value: unknown) => {
    logger.debug('[DataTable] handleFilterChange', { id, value })
    setFilterValues((prev) => ({ ...prev, [id]: value }))
    setPageIndex(0)
  }, [])

  const handleClearAllFilters = React.useCallback(() => {
    logger.debug('[DataTable] clearAllFilters')
    setFilterValues({})
    setGlobalQuery(undefined)
    setPageIndex(0)
  }, [])

  // Check if any filters are active (for empty state messaging)
  const hasActiveFilters = React.useMemo(() => {
    if (globalQuery) return true
    return Object.values(filterValues).some((v) => {
      if (v === undefined || v === null || v === '') return false
      if (typeof v === 'string' && v === 'all') return false
      if (typeof v === 'object') {
        const range = v as { from?: unknown; to?: unknown }
        return range.from !== undefined || range.to !== undefined
      }
      return true
    })
  }, [globalQuery, filterValues])

  const isVirtual = layout === 'virtual'
  const queryEnabled = (!!fetcher || !!data) && !isVirtual
  // Generate a stable unique ID for this table instance if no queryKey prop provided
  const tableId = React.useId()
  const queryKeyBase = queryKeyProp ?? `data-table-${tableId}`
  const queryKey = React.useMemo(() => {
    const keyObj = {
      pageIndex,
      pageSize,
      sorting,
      filters: debouncedFilterValues,
      q: debouncedGlobalQuery,
    }
    // Stable string for the dynamic params prevents accidental refetch loops
    const keyStr = JSON.stringify(keyObj)
    return [queryKeyBase, keyStr]
  }, [
    queryKeyBase,
    pageIndex,
    pageSize,
    sorting,
    debouncedFilterValues,
    debouncedGlobalQuery,
  ])

  const query = useQuery({
    queryKey,
    enabled: queryEnabled,
    queryFn: async () => {
      logger.debug('[DataTable] queryFn', {
        pageIndex,
        pageSize,
        sorting,
        filters: debouncedFilterValues,
        q: debouncedGlobalQuery,
        hasFetcher: !!fetcher,
        hasClientData: !!data,
      })
      if (fetcher) {
        const res = await fetcher({
          pageIndex,
          pageSize,
          sorting: sorting as unknown as Array<Sort>,
          filters: debouncedFilterValues,
          q: debouncedGlobalQuery,
        })
        logger.debug('[DataTable] queryFn result', {
          rows: res.rows.length,
          total: res.total,
          pageCount: res.pageCount,
        })
        return res
      }
      // client mode
      const rows = data ?? []
      const total = rows.length
      const start = pageIndex * pageSize
      const end = start + pageSize
      const clientRes = {
        rows: rows.slice(start, end),
        total,
        pageCount: Math.ceil(total / pageSize),
      }
      logger.debug('[DataTable] queryFn (client) result', {
        rows: clientRes.rows.length,
        total: clientRes.total,
        pageCount: clientRes.pageCount,
      })
      return clientRes
    },
    placeholderData: (previousData) => previousData,
    retry: false,
  })

  // Infinite mode for virtual list
  const infiniteEnabled = isVirtual && !!infiniteFetcher
  const infiniteKey = React.useMemo(() => {
    const keyObj = {
      pageSize,
      sorting,
      filters: debouncedFilterValues,
      q: debouncedGlobalQuery,
    }
    return [`${queryKeyBase}-infinite`, JSON.stringify(keyObj)]
  }, [
    queryKeyBase,
    pageSize,
    sorting,
    debouncedFilterValues,
    debouncedGlobalQuery,
  ])

  const infinite = useInfiniteQuery({
    queryKey: infiniteKey,
    enabled: infiniteEnabled,
    initialPageParam: undefined as unknown,
    getNextPageParam: (last) => (last as any)?.nextPageParam,
    queryFn: async ({ pageParam }) => {
      logger.debug('[DataTable] infinite queryFn', {
        pageParam,
        pageSize,
        sorting,
        filters: debouncedFilterValues,
        q: debouncedGlobalQuery,
        hasInfiniteFetcher: !!infiniteFetcher,
      })
      const res = await infiniteFetcher!({
        pageParam,
        pageSize,
        sorting: sorting as unknown as Array<Sort>,
        filters: debouncedFilterValues,
        q: debouncedGlobalQuery,
      })
      logger.debug('[DataTable] infinite result', {
        rows: res.rows.length,
        next: res.nextPageParam !== undefined,
      })
      return res
    },
    retry: false,
  })

  const flatInfiniteRows: Array<TData> = React.useMemo(
    () =>
      (infinite.data?.pages.flatMap((p: any) => p.rows) ?? []) as Array<TData>,
    [infinite.data?.pages],
  )

  const rows: Array<TData> = isVirtual
    ? flatInfiniteRows
    : (query.data?.rows ?? [])
  const total = isVirtual ? undefined : query.data?.total
  const pageCount = isVirtual ? undefined : query.data?.pageCount
  const isLoading =
    loading ?? (isVirtual ? infinite.isLoading : query.isLoading)
  const loadError =
    error ??
    (isVirtual
      ? infinite.error
        ? 'Failed to load data'
        : undefined
      : query.error
        ? 'Failed to load data'
        : undefined)
  const isRefetching = !isVirtual && query.isFetching && !query.isLoading

  const isSearchPending = globalQuery !== debouncedGlobalQuery
  // Check if filter values are pending debounce
  const isFiltersPending =
    JSON.stringify(filterValues) !== JSON.stringify(debouncedFilterValues)

  // When using a fetcher, sorting and filtering are handled server-side
  const isServerSide = !!fetcher || !!infiniteFetcher

  const table = useReactTable<TData>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
      columnSizing,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: setRowSelection,
    columnResizeMode: resizingMode,
    enableColumnResizing: true,
    enableRowSelection: selectable,
    getRowId: getRowId,
    filterFns: { fuzzy: fuzzyFilter },
    // Server-side sorting: don't re-sort client-side when fetcher handles sorting
    manualSorting: isServerSide,
    // Server-side filtering: filters are handled by the fetcher
    manualFiltering: isServerSide,
  })

  // Notify selection changes outward when requested
  React.useEffect(() => {
    if (!onSelectionChange) return
    const selectedRows = table.getSelectedRowModel().rows
    logger.debug('[DataTable] selection changed', {
      count: selectedRows.length,
    })
    onSelectionChange(selectedRows)
  }, [rowSelection])

  const clearSelection = React.useCallback(() => setRowSelection({}), [])

  // Track scroll position for sticky header shadow
  const [isScrolled, setIsScrolled] = React.useState(false)

  // Infinite scroll trigger on container scroll (virtual mode) + scroll tracking
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onScroll = () => {
      // Track if scrolled for header shadow
      setIsScrolled(el.scrollTop > 0)
      // Infinite scroll trigger
      if (isVirtual) {
        const distance = el.scrollHeight - el.scrollTop - el.clientHeight
        if (
          distance < 200 &&
          infinite.hasNextPage &&
          !infinite.isFetchingNextPage
        ) {
          logger.debug('[DataTable] near bottom -> fetchNextPage')
          infinite.fetchNextPage()
        }
      }
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [isVirtual, infinite.hasNextPage, infinite.isFetchingNextPage])

  return (
    <div className="w-full">
      <Toolbar
        table={table}
        globalSearch={globalSearch}
        globalQuery={globalQuery}
        onGlobalQueryChange={(v) => {
          setGlobalQuery(v)
          setPageIndex(0)
        }}
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        enableColumnVisibility={enableColumnVisibility}
        density={density}
        showDensityToggle={showDensityToggle}
        onDensityChange={(d) => setDensity(d)}
        isSearchPending={isSearchPending}
      />

      {inlineBulkBar &&
        selectable &&
        table.getSelectedRowModel().rows.length > 0 && (
          <div className="flex items-center justify-between border rounded-md px-3 py-2 mb-3">
            <div className="text-sm">
              <span className="font-medium">
                {table.getSelectedRowModel().rows.length}
              </span>{' '}
              {tUi('dataTable.selected')}
            </div>
            <div className="flex items-center gap-2">
              {bulkActions?.(table.getSelectedRowModel().rows, clearSelection)}
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                {tUi('dataTable.clearSelection')}
              </Button>
            </div>
          </div>
        )}

      <div ref={containerRef} className="w-full overflow-auto max-h-[70vh]">
        <div className="min-w-[720px] relative">
          {isRefetching && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10 rounded-md">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span>{tUi('dataTable.loading')}</span>
              </div>
            </div>
          )}
          {/* Ghost line indicator for column resizing */}
          <ColumnResizeIndicator table={table} selectable={selectable} />
          <Table
            style={{
              // Only use explicit column sizing when there's data to size from;
              // when empty, let table use natural 100% width to prevent overflow
              width: rows.length > 0 ? table.getCenterTotalSize() : undefined,
              minWidth: '100%',
              // Fixed layout only when we have data - allows proper column resizing
              // When empty, use auto layout to prevent overflow from default column sizes
              tableLayout: rows.length > 0 ? 'fixed' : 'auto',
            }}
          >
            <TableHeader
              className={`sticky top-0 z-10 bg-background transition-shadow ${isScrolled ? 'shadow-md' : ''}`}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {selectable && (
                    <TableHead className="w-12 bg-background">
                      <Checkbox
                        checked={
                          table.getIsAllPageRowsSelected() ||
                          (table.getIsSomePageRowsSelected() && 'indeterminate')
                        }
                        onCheckedChange={(v) =>
                          table.toggleAllPageRowsSelected(!!v)
                        }
                        aria-label={tUi('dataTable.selectAll')}
                      />
                    </TableHead>
                  )}
                  {headerGroup.headers.map((header) => {
                    const isResizing = header.column.getIsResizing()
                    const canResize = header.column.getCanResize()
                    const currentSize = header.getSize()
                    // Only apply explicit widths when we have data rows -
                    // when empty, let columns auto-size to prevent overflow
                    const hasData = rows.length > 0
                    return (
                      <TableHead
                        key={header.id}
                        style={hasData ? { width: currentSize } : undefined}
                        className="bg-background relative group"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : ''
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: ' ↑',
                              desc: ' ↓',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                        {/* Column resize handle */}
                        {canResize && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            onDoubleClick={() => header.column.resetSize()}
                            onClick={(e) => e.stopPropagation()}
                            className={`
                              absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none
                              transition-all duration-150
                              ${
                                isResizing
                                  ? 'bg-primary opacity-100'
                                  : 'bg-transparent hover:bg-border/80 opacity-0 group-hover:opacity-100'
                              }
                            `}
                            style={{ userSelect: 'none', touchAction: 'none' }}
                            aria-hidden
                            title={tUi('dataTable.resizeHint')}
                          />
                        )}
                      </TableHead>
                    )
                  })}
                  {rowActions && <TableHead className="w-10 bg-background" />}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {/* Show skeleton on initial load, refetching with empty data, or pending filter changes with empty data */}
              {isLoading ||
              ((isRefetching || isFiltersPending) && rows.length === 0) ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {selectable && (
                      <TableCell className="w-12">
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                    )}
                    {columns.map((_col, j) => (
                      <TableCell key={`skc-${i}-${j}`}>
                        <Skeleton className="h-4 w-full max-w-[120px]" />
                      </TableCell>
                    ))}
                    {rowActions && (
                      <TableCell className="w-10">
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : loadError ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                  >
                    <div className="flex flex-col items-center justify-center py-8 gap-3">
                      <AlertCircle className="h-10 w-10 text-destructive/60" />
                      <div className="text-sm text-destructive">
                        {loadError}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => query.refetch()}
                        className="gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        {tUi('dataTable.retry')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                  >
                    {emptyState ?? (
                      <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <Inbox className="h-12 w-12 text-muted-foreground/40" />
                        <div className="text-center">
                          <p className="text-sm font-medium text-muted-foreground">
                            {tUi('dataTable.emptyTitle')}
                          </p>
                          {hasActiveFilters && (
                            <p className="text-xs text-muted-foreground/80 mt-1">
                              {tUi('dataTable.emptyHint')}
                            </p>
                          )}
                        </div>
                        {hasActiveFilters && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearAllFilters}
                          >
                            {tUi('dataTable.clearFilters')}
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                table
                  .getRowModel()
                  .rows.map((row) => (
                    <DataTableRow
                      key={row.id}
                      row={row}
                      density={density}
                      selectable={selectable}
                      rowActions={rowActions}
                      onRowClick={onRowClick}
                      isSelected={row.getIsSelected()}
                    />
                  ))
              )}
              {isVirtual && (
                <TableRow>
                  <TableCell
                    colSpan={
                      columns.length +
                      (selectable ? 1 : 0) +
                      (rowActions ? 1 : 0)
                    }
                  >
                    <div className="py-3 flex items-center justify-center">
                      {infinite.isFetchingNextPage ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Spinner className="h-4 w-4" />{' '}
                          {tUi('dataTable.loadingMore')}
                        </div>
                      ) : infinite.hasNextPage ? (
                        <div className="text-sm text-muted-foreground">
                          {tUi('dataTable.scrollToLoad')}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {tUi('dataTable.endOfList')}
                        </div>
                      )}
                      {/* Sentinel div to be observed externally if needed */}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {layout === 'paginated' && (
        <div className="py-3">
          <DataTablePagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={pageCount}
            total={total}
            pageSizeOptions={pageSizeOptions}
            onPageChange={(i) => setPageIndex(i)}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPageIndex(0)
            }}
          />
        </div>
      )}
    </div>
  )
}
